import React from 'react';
import { CheckCircle2, XCircle, Minus, RefreshCw, Trash2, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { QAIssue } from '@/services/kbService';

export type ResolveAction = 'accept' | 'reject' | 'ignore' | 'remove' | 'merge';

/**
 * Determines whether the given issue is a misclassification (entity type) issue.
 * Checks if the issue_type string contains "misclassif" or "type".
 */
function isMisclassificationIssue(issue: QAIssue): boolean {
  const issueType = (issue.issue_type || '').toLowerCase();
  return issueType.includes('misclassif') || issueType.includes('type');
}

export interface QAResolveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: QAIssue | null;
  action: ResolveAction;
  notes: string;
  resolving: boolean;
  onActionChange: (action: ResolveAction) => void;
  onNotesChange: (notes: string) => void;
  onResolve: () => void;
  applyToSimilar?: boolean;
  onApplyToSimilarChange?: (checked: boolean) => void;
  similarCount?: number;
  /** Valid entity types for misclassification correction dropdown */
  validEntityTypes?: string[];
  /** Currently selected corrected entity type */
  correctedType?: string;
  /** Callback when corrected entity type changes */
  onCorrectedTypeChange?: (type: string) => void;
}

export const QAResolveModal: React.FC<QAResolveModalProps> = ({
  open,
  onOpenChange,
  issue,
  action,
  notes,
  resolving,
  onActionChange,
  onNotesChange,
  onResolve,
  applyToSimilar = false,
  onApplyToSimilarChange,
  similarCount = 0,
  validEntityTypes = [],
  correctedType = '',
  onCorrectedTypeChange,
}) => {
  const showEntityTypePicker =
    issue != null &&
    action === 'accept' &&
    validEntityTypes.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground max-w-[95vw] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Resolve Issue
          </DialogTitle>
        </DialogHeader>

        {issue && (
          <div className="space-y-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-400">{issue.description || 'No description available'}</p>
            </div>

            <div className="flex items-center gap-2">
              <span className="font-medium">Entity: </span>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                {issue.entity_type || 'Unknown'}
              </Badge>
              <span>{issue.entity_value || 'N/A'}</span>
            </div>

            {issue.suggested_correction && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <span className="font-medium text-emerald-400">Suggested Correction: </span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                  {issue.suggested_correction}
                </Badge>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <Label>Resolution Action:</Label>
              <Select value={action} onValueChange={(v) => onActionChange(v as ResolveAction)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark">
                  <SelectItem value="accept">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                      Accept - Apply suggested correction
                    </div>
                  </SelectItem>
                  <SelectItem value="reject">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-400" />
                      Reject - Keep original value
                    </div>
                  </SelectItem>
                  <SelectItem value="ignore">
                    <div className="flex items-center gap-2">
                      <Minus className="h-4 w-4 text-muted-foreground" />
                      Ignore - Mark as not an issue
                    </div>
                  </SelectItem>
                  {issue?.issue_type === 'duplicate' && (
                    <SelectItem value="merge">
                      <div className="flex items-center gap-2">
                        <GitMerge className="h-4 w-4 text-purple-400" />
                        Merge - Merge duplicate into primary entity
                      </div>
                    </SelectItem>
                  )}
                  {(issue?.issue_type === 'invalid_entry' || issue?.issue_type === 'noise') && (
                    <SelectItem value="remove">
                      <div className="flex items-center gap-2">
                        <Trash2 className="h-4 w-4 text-red-400" />
                        Remove - Permanently delete this entity
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {showEntityTypePicker && (
              <div className="space-y-2">
                <Label>Correct Entity Type:</Label>
                <Select
                  value={correctedType}
                  onValueChange={(v) => onCorrectedTypeChange?.(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select the correct entity type..." />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {validEntityTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {issue.entity_type && correctedType && correctedType !== issue.entity_type && (
                  <p className="text-xs text-muted-foreground">
                    Reclassifying from{' '}
                    <Badge variant="outline" className="text-xs bg-destructive/10 text-red-400 border-destructive/30">
                      {issue.entity_type}
                    </Badge>
                    {' '}to{' '}
                    <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                      {correctedType}
                    </Badge>
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional):</Label>
              <Textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Add notes about this resolution..."
                rows={3}
                className="resize-none"
              />
            </div>

            {similarCount > 0 && onApplyToSimilarChange && (
              <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="apply-to-similar"
                    checked={applyToSimilar}
                    onCheckedChange={(checked) =>
                      onApplyToSimilarChange(checked === true)
                    }
                  />
                  <Label
                    htmlFor="apply-to-similar"
                    className="text-sm font-medium cursor-pointer leading-none"
                  >
                    Apply to all similar issues
                  </Label>
                </div>
                <p className="text-xs text-primary ml-6">
                  Will resolve {similarCount} other pending{' '}
                  {issue.issue_type ? `"${issue.issue_type}"` : ''} issue
                  {similarCount !== 1 ? 's' : ''} for{' '}
                  {issue.entity_type ? `"${issue.entity_type}"` : 'this'} entities
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={resolving}>
                {resolving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                {applyToSimilar && similarCount > 0
                  ? `Resolve ${similarCount + 1} Issues`
                  : 'Resolve'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark bg-background text-foreground">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Resolution</AlertDialogTitle>
                <AlertDialogDescription>
                  {applyToSimilar && similarCount > 0
                    ? <>Are you sure you want to resolve <strong>{similarCount + 1}</strong> issues ({similarCount} similar + this one)?</>
                    : <>Are you sure you want to resolve this issue for <strong>{issue?.entity_value}</strong>?</>
                  }
                  {' '}Action: <strong>{action}</strong>.{' '}
                  {correctedType && correctedType !== issue?.entity_type && (
                    <>Type will change from <strong>{issue?.entity_type}</strong> to <strong>{correctedType}</strong>.</>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onResolve}>
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
